export interface ApiErrorBody {
  error: { code: string; message: string; requestId?: string };
}

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/**
 * Whether sending the same request again could succeed: the network dropped,
 * the server failed, or it asked to be tried later. A refusal of the request
 * itself — invalid, gone, not allowed, a conflict — answers the same way twice.
 */
export function isTransient(err: unknown): boolean {
  if (!(err instanceof ApiRequestError)) return true;
  return err.status >= 500 || err.status === 408 || err.status === 429;
}

let csrfToken: string | null = null;
export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined) headers.set('content-type', 'application/json');
  if (csrfToken && init.method && init.method !== 'GET') {
    headers.set('x-csrf-token', csrfToken);
  }
  const res = await fetch(`/api/v1${path}`, { credentials: 'same-origin', ...init, headers });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiRequestError(
      res.status,
      body?.error.code ?? 'unknown',
      body?.error.message ?? 'Prišlo je do napake. Poskusi znova.',
      body?.error.requestId,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
